import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";
import { getDvSourceImageUrl, ensureThemeJsonArray } from "../../functions/themeAssets";
import "../fonts/breuer-headline.css";
import "./game.css";
import Swal from "sweetalert2";
import pointIcon from "./img/point.png";
import hintActionIcon from "./img/hint.png";

/** Must match `welcome-bowl-perfect-float` animation duration in `game.css` (1.35s). */
const PERFECT_SALAD_BOWL_FLOAT_MS = 1350;

const NAMED_THEME_COLORS = {
  black: "#000000",
  white: "#ffffff",
  red: "#ff0000",
  green: "#008000",
  blue: "#0000ff",
  yellow: "#ffff00",
  gray: "#808080",
  grey: "#808080",
};

/** Resolves theme DB colors (hex or named) for inline CSS; returns undefined if unset/invalid. */
function themeColorToCss(val) {
  if (val == null || val === "") return undefined;
  if (typeof val !== "string") return undefined;
  const v = val.trim();
  if (!v) return undefined;
  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v.toLowerCase();
  if (/^#[0-9a-fA-F]{8}$/.test(v)) return `#${v.slice(1, 7).toLowerCase()}`;
  const named = NAMED_THEME_COLORS[v.toLowerCase()];
  if (named) return named;
  return undefined;
}

function getViewportSizeForLayout() {
  const iw = window.innerWidth || 0;
  const ih = window.innerHeight || 0;
  const vv = typeof window !== "undefined" ? window.visualViewport : null;
  const isPortrait =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(orientation: portrait)").matches;
  const isLandscape =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(orientation: landscape)").matches;
  /** Short edge — phones in landscape stay ≤ ~480; avoids treating desktop/large tablet as “phone”. */
  const compactLandscapePhone = isLandscape && Math.min(iw, ih) <= 560;

  if (vv && vv.width > 0 && vv.height > 0) {
    if (isPortrait && iw <= 768) {
      return { vw: vv.width, vh: vv.height };
    }
    if (compactLandscapePhone) {
      return { vw: vv.width, vh: vv.height };
    }
  }
  return { vw: iw, vh: ih };
}

/** Parse DB / HUD clock strings (MM:SS or HH:MM:SS) to seconds for the welcome timer. */
function parseClockToSeconds(clock) {
  const s = String(clock ?? "").trim();
  if (!s) return 0;
  const parts = s.split(":").map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

function normalizePlayerName(user) {
  if (!user || typeof user !== "object") return "";
  const raw = user.name;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t && !/^null(\s+null)*$/i.test(t)) return t;
  }
  const fn = user.firstName ?? user.first_name ?? "";
  const ln = user.lastName ?? user.last_name ?? "";
  const parts = [fn, ln].filter(
    (p) =>
      p != null &&
      String(p).trim() !== "" &&
      String(p).trim().toLowerCase() !== "null"
  );
  return parts.map((p) => String(p).trim()).join(" ").trim();
}

function Welcome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { data: themeData, status: themeStatus, currentTheme } = useSelector((state) => state.theme);

  const [points, setPoints] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  const [isMobileLandscape, setIsMobileLandscape] = useState(false);

  const [allItems, setAllItems] = useState([]);
  const [imagesDropped, setImagesDropped] = useState(0);
  const [droppedByCategory, setDroppedByCategory] = useState({ 1: [], 2: [], 3: [] });
  const [draggedDishId, setDraggedDishId] = useState(null);
  const [bowlDragOver, setBowlDragOver] = useState(false);
  const [droppedPositiveDishIds, setDroppedPositiveDishIds] = useState([]);
  const [bowlLayeredItems, setBowlLayeredItems] = useState([]);
  const [activeDishPopupImage, setActiveDishPopupImage] = useState("");
  const [activeDishPopupText, setActiveDishPopupText] = useState("");
  const [wrongDropDishId, setWrongDropDishId] = useState(null);
  const [animatingSosId, setAnimatingSosId] = useState("");
  const [showRulesPopup, setShowRulesPopup] = useState(false);
  const [openedRecipeHintsByRound, setOpenedRecipeHintsByRound] = useState({});
  const [popupBubbleKey, setPopupBubbleKey] = useState(0);
  const [bowlPointsFloat, setBowlPointsFloat] = useState(null);
  const [perfectSaladBowlFloat, setPerfectSaladBowlFloat] = useState(null);
  const [interRoundOverlay, setInterRoundOverlay] = useState(false);
  const [activeRoundIndex, setActiveRoundIndex] = useState(0);
  /** DB `stages.currentQues`: rounds completed; resume at round index = this value. Set from fetchReport until applied. */
  const [resumeStageCount, setResumeStageCount] = useState(null);
  const [isStageTransitioning, setIsStageTransitioning] = useState(false);

  const headerSet = useRef(false);
  const pointsRef = useRef(0);
  const totalImagesCount = useRef(0);
  const dishPopupTimerRef = useRef(null);
  const bowlFloatTimerRef = useRef(null);
  const perfectSaladTimerRef = useRef(null);
  const droppedPositiveDishIdsRef = useRef([]);
  const bowlLayeredItemsRef = useRef([]);
  const isStageTransitioningRef = useRef(false);

  const reportIdRef = useRef(null);
  const stageRowIdRef = useRef(null);
  const bowlDropZoneRef = useRef(null);
  const pointerSessionRef = useRef(null);
  const stageConfigAlertShownRef = useRef(false);

  const storedUserData = JSON.parse(
    sessionStorage.getItem("userData") || "{}"
  ) || {};

  const sid = String(storedUserData?.sessionId ?? "").toLowerCase();
  const oid = String(storedUserData?.organizationId ?? "").toLowerCase();
  /** Matches DB `user_type` / login `role` (e.g. demobypass). */
  const reportRoleForApi =
    storedUserData.user_type ||
    storedUserData.userType ||
    storedUserData.role ||
    (storedUserData.source === "DEMO" ? "demobypass" : null) ||
    storedUserData.source ||
    "ORG_USER";
  const isDemoBypass =
    String(reportRoleForApi).toLowerCase() === "demobypass" ||
    storedUserData?.source === "DEMO" ||
    (sid === "demobypass" && oid === "demobypass");
  const roundListForState = ensureThemeJsonArray(themeData?.rounds);
  const stageCountRaw = Number(themeData?.stage_count);
  const expectedStageCount =
    Number.isFinite(stageCountRaw) && stageCountRaw > 0
      ? Math.floor(stageCountRaw)
      : roundListForState.length;
  const hasStageRoundMismatch = roundListForState.length < expectedStageCount;
  const requiredRoundImageLabels = {
    hint_image: "hint image",
    required_recipe_image: "required recipe image",
    sos1_layered_image: "SOS 1 layered image",
    sos2_layered_image: "SOS 2 layered image",
    sos3_layered_image: "SOS 3 layered image",
  };
  const missingRoundImageIssues = [];
  roundListForState.slice(0, expectedStageCount).forEach((round, roundIdx) => {
    Object.entries(requiredRoundImageLabels).forEach(([key, label]) => {
      if (!String(round?.[key] || "").trim()) {
        missingRoundImageIssues.push(`Stage ${roundIdx + 1} lacks a ${label}.`);
      }
    });
    const dishes = ensureThemeJsonArray(round?.dishes);
    if (!dishes.length) {
      missingRoundImageIssues.push(`Stage ${roundIdx + 1} has no dishes configured.`);
      return;
    }
    dishes.forEach((dish, dishIdx) => {
      if (!String(dish?.dish_img || "").trim()) {
        missingRoundImageIssues.push(
          `Stage ${roundIdx + 1} Dish ${dishIdx + 1} lacks a dish image.`
        );
      }
      if (
        String(dish?.type || "").toLowerCase() === "positive" &&
        !String(dish?.dish_layered_img || "").trim()
      ) {
        missingRoundImageIssues.push(
          `Stage ${roundIdx + 1} Dish ${dishIdx + 1} lacks a layered dish image.`
        );
      }
    });
  });
  const hasMissingRoundImages = missingRoundImageIssues.length > 0;
  const hasStageConfigIssue = hasStageRoundMismatch || hasMissingRoundImages;

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: currentTheme || null }));
  }, [dispatch, currentTheme]);

  useEffect(() => {
    if (headerSet.current) return;
    const baseUrl = String(process.env.REACT_APP_BASE_URL || "").trim();
    dispatch(setBackButtonUrl(baseUrl || "/"));
    headerSet.current = true;
  }, [dispatch]);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);
  useEffect(() => {
    droppedPositiveDishIdsRef.current = droppedPositiveDishIds;
  }, [droppedPositiveDishIds]);
  useEffect(() => {
    bowlLayeredItemsRef.current = bowlLayeredItems;
  }, [bowlLayeredItems]);
  useEffect(() => {
    isStageTransitioningRef.current = isStageTransitioning;
  }, [isStageTransitioning]);

  const setStageTransitioning = (next) => {
    isStageTransitioningRef.current = next;
    setIsStageTransitioning(next);
  };

  const wrongCountRef = useRef(0);
  useEffect(() => {
    wrongCountRef.current = wrongCount;
  }, [wrongCount]);

  const elapsedTimeRef = useRef(0);
  useEffect(() => {
    elapsedTimeRef.current = elapsedTime;
  }, [elapsedTime]);

  /** Wrong bowl drops in the current round only (for perfect-round bonus). */
  const wrongDropsThisRoundRef = useRef(0);

  useEffect(() => {
    return () => {
      if (dishPopupTimerRef.current) clearTimeout(dishPopupTimerRef.current);
      if (bowlFloatTimerRef.current) clearTimeout(bowlFloatTimerRef.current);
      if (perfectSaladTimerRef.current) clearTimeout(perfectSaladTimerRef.current);
      const sess = pointerSessionRef.current;
      if (sess?.ghostEl?.parentNode) {
        sess.ghostEl.parentNode.removeChild(sess.ghostEl);
      }
      pointerSessionRef.current = null;
    };
  }, []);

  useEffect(() => {
    const body = document.body;

    body.classList.add("game-active");
    body.style.setProperty("backdrop-filter", "none");

    return () => {
      body.classList.remove("game-active");
      body.style.removeProperty("backdrop-filter");
    };
  }, []);

  useEffect(() => {
    if (themeStatus === "failed") {
      navigate("/login", { replace: true });
    }
  }, [themeStatus, navigate]);

  useEffect(() => {
    if (!themeData || !hasStageConfigIssue) {
      stageConfigAlertShownRef.current = false;
      return;
    }
    if (stageConfigAlertShownRef.current) return;
    stageConfigAlertShownRef.current = true;
    const firstMissingImageIssue = missingRoundImageIssues[0];
    const stageMismatchText = hasStageRoundMismatch
      ? `The theme defines ${expectedStageCount} stages but includes only ${roundListForState.length} round entries.`
      : `The theme defines ${expectedStageCount} stages,`;
    const missingImagesText = hasMissingRoundImages
      ? ` but required image data is missing. ${firstMissingImageIssue}${
          missingRoundImageIssues.length > 1
            ? ` (+${missingRoundImageIssues.length - 1} more)`
            : ""
        }.`
      : "";
    Swal.fire({
      icon: "warning",
      title: "Stage data missing",
      text: `${stageMismatchText}${missingImagesText} Please complete all stage data before starting the game.`,
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      const baseUrl = String(process.env.REACT_APP_BASE_URL || "").trim();
      window.location.assign(baseUrl || "/");
    });
  }, [
    themeData,
    hasStageConfigIssue,
    hasMissingRoundImages,
    expectedStageCount,
    roundListForState.length,
    missingRoundImageIssues,
  ]);

  useEffect(() => {
    if (!storedUserData?.sessionId || isDemoBypass) return;
    const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    if (!uid || !sid || !oid || !adminToken) return;

    const syncReportAndMaybeRedirect = async () => {
      const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
      if (!backendBase) return;

      const playerName = normalizePlayerName(storedUserData);

      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId: sid,
            organizationId: oid,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!data.success) return;

        if (data.report) {
          if (Number(data.report.gameover) === 1) {
            navigate("/thankyou", {
              replace: true,
              state: {
                points: Number(data.report.points) || 0,
                time: data.report.time ?? "00:00",
              },
            });
            return;
          }

          const dbTime = data.report.time;
          const normalizedTime = String(dbTime ?? "").trim();
          const isZeroTime =
            !normalizedTime ||
            normalizedTime === "00:00" ||
            normalizedTime === "0" ||
            normalizedTime === "00:00:00" ||
            normalizedTime === "0:00";

          const cqPre = Number(data.report.currentQues);
          const midSessionResume =
            Number.isFinite(cqPre) && cqPre > 0 && Number(data.report.gameover) !== 1;

          if (!isZeroTime && !midSessionResume) {
            navigate("/thankyou", {
              replace: true,
              state: {
                points: Number(data.report.points) || 0,
                time: dbTime,
              },
            });
            return;
          }

          const stageId = data.report.id;
          if (stageId != null && stageId !== "") {
            stageRowIdRef.current = String(stageId);
          }
          const cq = Number(data.report.currentQues);
          if (Number.isFinite(cq) && cq > 0) {
            setResumeStageCount(Math.floor(cq));
            const rp = Number(data.report.points);
            const nextPoints = Number.isFinite(rp) ? rp : 0;
            setPoints(nextPoints);
            pointsRef.current = nextPoints;
            const rw = Number(data.report.wrongcount);
            setWrongCount(Number.isFinite(rw) ? rw : 0);
            setElapsedTime(parseClockToSeconds(dbTime));
          }
          const extRid = data.report.reportId;
          if (extRid != null && extRid !== "") {
            reportIdRef.current = String(extRid);
            return;
          }
          /* In-progress row but no external reportId yet — seed + link below. Never use local stages.id as reportId. */
        } else {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId: sid,
              organizationId: oid,
              email: storedUserData.email || "",
              emp_id: storedUserData.employeeId || storedUserData.emp_id || "",
              name: playerName,
              user_type: reportRoleForApi,
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            stageRowIdRef.current = String(startData.id);
          }
        }

        if (reportIdRef.current) return;

        const role = reportRoleForApi;
        const seedRes = await sendReport({
          sessionId: sid,
          organizationId: oid,
          role,
          token: storedUserData.token || "",
          points: 0,
          time: "00:00",
          gameId: storedUserData.gameId,
          name: playerName,
          userId: uid,
        });
        const newRid = extractReportIdFromResponse(seedRes);
        if (newRid != null && newRid !== "") {
          reportIdRef.current = String(newRid);
          if (stageRowIdRef.current) {
            try {
              await fetch(`${backendBase}/welcomeStageLinkReport`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${adminToken}`,
                },
                body: JSON.stringify({
                  userId: uid,
                  sessionId: sid,
                  organizationId: oid,
                  stageId: stageRowIdRef.current,
                  reportId: String(newRid),
                }),
              });
            } catch (linkErr) {
              console.error("welcomeStageLinkReport:", linkErr);
            }
          }
        }
      } catch (error) {
        console.error("Report sync error:", error);
      }
    };

    syncReportAndMaybeRedirect();
  }, [adminToken, isDemoBypass, navigate]);

  useEffect(() => {
    if (!themeData) return;

    const compute = () => {
      const { vw, vh } = getViewportSizeForLayout();
      const shortSide = Math.min(vw, vh);
      /** Portrait + narrow width → mobile portrait art. */
      const mobilePortrait =
        window.innerWidth <= 768 &&
        window.matchMedia &&
        window.matchMedia("(orientation: portrait)").matches;
      const mobileLandscape =
        window.matchMedia &&
        window.matchMedia("(orientation: landscape)").matches &&
        shortSide <= 480;
      setIsMobilePortrait(mobilePortrait);
      setIsMobileLandscape(mobileLandscape);

    };

    compute();
    window.addEventListener("resize", compute);
    window.addEventListener("orientationchange", compute);
    const vv = window.visualViewport;
    if (vv) {
      vv.addEventListener("resize", compute);
      vv.addEventListener("scroll", compute);
    }

    return () => {
      window.removeEventListener("resize", compute);
      window.removeEventListener("orientationchange", compute);
      if (vv) {
        vv.removeEventListener("resize", compute);
        vv.removeEventListener("scroll", compute);
      }
    };
  }, [themeData]);

  useEffect(() => {
    if (!themeData) return;

    const c1 = ensureThemeJsonArray(themeData.category1_items);
    const c2 = ensureThemeJsonArray(themeData.category2_items);
    const c3 = ensureThemeJsonArray(themeData.category3_items);

    const images = [];
    c1.forEach((imgPath) =>
      images.push({ id: `img-c1-${images.length}-${Date.now()}`, url: getDvSourceImageUrl(imgPath), category: 1 })
    );
    c2.forEach((imgPath) =>
      images.push({ id: `img-c2-${images.length}-${Date.now()}`, url: getDvSourceImageUrl(imgPath), category: 2 })
    );
    c3.forEach((imgPath) =>
      images.push({ id: `img-c3-${images.length}-${Date.now()}`, url: getDvSourceImageUrl(imgPath), category: 3 })
    );

    totalImagesCount.current = images.length;

    const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
    setAllItems(shuffle(images));
  }, [themeData]);


  useEffect(() => {
    if (!themeData || gameComplete || hasStageConfigIssue) return;
    const timer = setInterval(() => setElapsedTime((p) => p + 1), 1000);
    return () => clearInterval(timer);
  }, [themeData, gameComplete, hasStageConfigIssue]);

  useEffect(() => {
    const len = roundListForState.length;
    if (!len) return;
    if (resumeStageCount != null) {
      const idx = Math.min(Math.max(0, resumeStageCount), len - 1);
      setActiveRoundIndex(idx);
      setResumeStageCount(null);
      return;
    }
    setActiveRoundIndex((prev) => Math.min(prev, len - 1));
  }, [roundListForState.length, resumeStageCount]);

  const persistWelcomeStageProgress = async (completedRoundCount) => {
    if (isDemoBypass) return;
    const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
    const sid = storedUserData.sessionId;
    const oid = storedUserData.organizationId;
    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (!backendBase || !stageRowIdRef.current || !uid || !sid || !oid || !adminToken) return;
    const et = elapsedTimeRef.current;
    const timeStr = `${String(Math.floor(et / 60)).padStart(2, "0")}:${String(et % 60).padStart(2, "0")}`;
    try {
      await fetch(`${backendBase}/welcomeStageProgress`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          userId: uid,
          sessionId: sid,
          organizationId: oid,
          stageId: stageRowIdRef.current,
          currentQues: completedRoundCount,
          total_score: pointsRef.current,
          wrongcount: wrongCountRef.current,
          time: timeStr,
        }),
      });
    } catch (e) {
      console.error("welcomeStageProgress:", e);
    }
  };

  useEffect(() => {
    wrongDropsThisRoundRef.current = 0;
    droppedPositiveDishIdsRef.current = [];
    setDroppedPositiveDishIds([]);
    bowlLayeredItemsRef.current = [];
    setBowlLayeredItems([]);
    setActiveDishPopupImage("");
    setActiveDishPopupText("");
    setWrongDropDishId(null);
    setAnimatingSosId("");
    setDraggedDishId(null);
    setBowlDragOver(false);
    const sess = pointerSessionRef.current;
    if (sess?.ghostEl?.parentNode) {
      sess.ghostEl.parentNode.removeChild(sess.ghostEl);
    }
    pointerSessionRef.current = null;
  }, [activeRoundIndex]);

  const formatTime = () => {
    const min = Math.floor(elapsedTime / 60);
    const sec = elapsedTime % 60;
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  };

  const completeGame = async () => {
    if (gameComplete) return;
    setGameComplete(true);

    const currentPoints = pointsRef.current;

    try {
      if (!isDemoBypass) {
        const uid = storedUserData.userId || storedUserData.userid || storedUserData.id;
        const sid = storedUserData.sessionId;
        const oid = storedUserData.organizationId;
        const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
        const finishedTime = formatTime();

        if (backendBase && stageRowIdRef.current && uid && sid && oid && adminToken) {
          try {
            await fetch(`${backendBase}/welcomeStageComplete`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${adminToken}`,
              },
              body: JSON.stringify({
                userId: uid,
                sessionId: sid,
                organizationId: oid,
                stageId: stageRowIdRef.current,
                time: finishedTime,
                total_score: currentPoints,
                wrongcount: wrongCountRef.current,
                currentQues: Math.max(1, ensureThemeJsonArray(themeData?.rounds).length),
              }),
            });
          } catch (stageErr) {
            console.error("welcomeStageComplete:", stageErr);
          }
        }

        const role = reportRoleForApi;
        await sendReport({
          ...(reportIdRef.current ? { reportId: reportIdRef.current } : {}),
          sessionId: sid,
          organizationId: oid,
          role,
          token: storedUserData.token || "",
          points: currentPoints,
          time: finishedTime,
          gameId: storedUserData.gameId,
          name: normalizePlayerName(storedUserData),
          userId: uid,
        });
      }
    } catch (err) {
      console.error("Score update failed:", err);
    }

    setTimeout(() => {
      navigate("/thankyou", {
              state: { points: currentPoints, time: formatTime() },
            });
    }, 1500);

    // Swal.fire({
    //   icon: "success",
    //   title: "Game Completed!",
    //   confirmButtonText: "OK",
    //   allowOutsideClick: false,
    //   allowEscapeKey: false,
    // }).then((result) => {
    //   if (result.isConfirmed) {
    //     navigate("/thankyou", {
    //       state: { points: currentPoints, time: formatTime() },
    //     });
    //   }
    // });
  };

  const adminRightPoints = parseInt(themeData?.points, 10) || 1;

  if (themeStatus === "loading" || themeStatus === "idle" || !themeData) {
    return (
      <div className="fullhouse-page">
        <div className="fullhouse-loader">
          {/* <div className="quiz-loader" /> */}
        </div>
      </div>
    );
  }

  if (hasStageConfigIssue) {
    return (
      <div className="fullhouse-page">
        {/* <div className="fullhouse-loader" style={{ color: "#fff", textAlign: "center", padding: "24px" }}>
          Theme setup incomplete. Please add all stage rounds in Theme Update to start the game.
        </div> */}
      </div>
    );
  }

  const viewBgDesktop = getDvSourceImageUrl(themeData.desktop_view_image);
  const viewBgMobile = getDvSourceImageUrl(themeData.mobile_view_image);
  const viewBg = isMobilePortrait ? viewBgMobile : viewBgDesktop;
  const tableImage = getDvSourceImageUrl(themeData.table_image);
  const bowlImage = getDvSourceImageUrl(themeData.bowl_image);
  const plateImage = getDvSourceImageUrl(themeData.plate_image);
  const sos1Image = getDvSourceImageUrl(themeData.sos1_image);
  const sos2Image = getDvSourceImageUrl(themeData.sos2_image);
  const sos3Image = getDvSourceImageUrl(themeData.sos3_image);

  const cat1Img = getDvSourceImageUrl(themeData.category1_image);
  const cat2Img = getDvSourceImageUrl(themeData.category2_image);
  const cat3Img = getDvSourceImageUrl(themeData.category3_image);
  const roundList = roundListForState;
  const activeRound = roundList[activeRoundIndex] || roundList[0] || {};
  const activeRoundKey = String(activeRound?.round_id ?? activeRoundIndex + 1);
  const rulesList = ensureThemeJsonArray(themeData?.rules).filter(
    (r) => typeof r === "string" && r.trim()
  );
  const sos1LayeredImage = getDvSourceImageUrl(
    activeRound?.sos1_layered_image || themeData.sos1_layered_image
  );
  const sos2LayeredImage = getDvSourceImageUrl(
    activeRound?.sos2_layered_image || themeData.sos2_layered_image
  );
  const sos3LayeredImage = getDvSourceImageUrl(
    activeRound?.sos3_layered_image || themeData.sos3_layered_image
  );
  const activeRoundDishes = ensureThemeJsonArray(activeRound?.dishes);
  const popupBgImage = getDvSourceImageUrl(activeRound?.hint_image);
  /**
   * Semicircular (U-shape) arc layout for 1–5 dishes — outer dishes raised,
   * middle dish lowest. Computed via parabola so 3 / 4 / 5 dishes all curve.
   */
  const computeDishArcPositions = (count) => {
    if (!count || count <= 0) return [];
    if (count === 1) return [{ left: "50%", top: "55%" }];
    // Wide U on the central wood only — not the teal side wings (avoids labels on the floor).
    const xStart = 25;
    const xEnd   = 75;
    const yEdge   = 42; // outer dishes (higher in the arc — smaller % = higher on screen)
    const yCenter = 55; // center dish lowest, but shallow enough to stay on the counter
    return Array.from({ length: count }, (_, i) => {
      const t    = i / (count - 1);
      const left = xStart + (xEnd - xStart) * t;
      const norm = 2 * t - 1;           // –1 at left edge, 0 at centre, +1 at right edge
      const top  = yEdge + (yCenter - yEdge) * (1 - norm * norm); // parabola: low centre, high edges
      return { left: `${left}%`, top: `${top}%` };
    });
  };
  const visibleDishes = activeRoundDishes.slice(0, 5);
  const dishArcPositions = computeDishArcPositions(visibleDishes.length);
  const visibleDishCards = visibleDishes.map((dish, idx) => ({
    ...dish,
    __dishId: `dish-${idx}-${String(dish?.dish_name || "item").toLowerCase().replace(/\s+/g, "-")}`,
  }));
  const stageRequiredPositiveCount = visibleDishCards.filter(
    (dish) => String(dish?.type || "").toLowerCase() === "positive"
  ).length;
  const stageRequiredSosIds = [
    ["sos-1", sos1LayeredImage],
    ["sos-2", sos2LayeredImage],
    ["sos-3", sos3LayeredImage],
  ]
    .filter(([, src]) => Boolean(src))
    .map(([id]) => id);
  const stageCompletionTarget = stageRequiredPositiveCount + stageRequiredSosIds.length;

  const hudTitleBoxBg = themeData?.belt_color || "#c4a574";
  const gameTextColorCss = themeColorToCss(themeData?.game_text_color);
  const landingTitleColorCss = themeColorToCss(themeData?.landing_page_title_color);

  const titleDesktopFontSizePx = (() => {
    const n = parseInt(themeData?.landing_page_title_desktop_font_size_px, 10);
    if (!Number.isFinite(n)) return null;
    return Math.min(200, Math.max(8, n));
  })();

  /** Demo / demobypass: after the first correct bowl placement, send user to plans (not on wrong drops). */
  const redirectDemoUserToPlans = () => {
    setTimeout(() => {
      Swal.fire({
        icon: "info",
        text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
        confirmButtonText: "OK",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then(() => {
        window.location.assign("https://extramileplay.com/plans");
      });
    }, 400);
  };

  const sortItemByClick = (item) => {
    if (gameComplete || !item) return;
    const dropTarget = item.category;
    const nextDroppedTotal = imagesDropped + 1;
    setPoints((p) => p + adminRightPoints);
    setDroppedByCategory((prev) => {
      const cur = prev?.[dropTarget] || [];
      if (cur.some((x) => x.id === item.id)) return prev;
      return { ...prev, [dropTarget]: [...cur, item] };
    });
    setAllItems((prev) => prev.filter((i) => i.id !== item.id));
    setImagesDropped((d) => d + 1);

    if (nextDroppedTotal >= totalImagesCount.current) {
      setTimeout(() => completeGame(), 300);
    }
  };

  const handleHintClick = () => {
    if (!activeRound?.required_recipe_image) return;
    setOpenedRecipeHintsByRound((prev) => ({ ...prev, [activeRoundKey]: true }));
  };

  /** If this round had no wrong drops, add `bonus_point` from theme (admin: 1–100). */
  const maybeAwardPerfectRoundBonus = () => {
    if (wrongDropsThisRoundRef.current !== 0) return 0;
    const raw = parseInt(themeData?.bonus_point, 10);
    if (!Number.isFinite(raw) || raw < 1) return 0;
    const bonus = Math.min(100, Math.max(1, raw));
    const next = pointsRef.current + bonus;
    pointsRef.current = next;
    setPoints(next);
    if (perfectSaladTimerRef.current) clearTimeout(perfectSaladTimerRef.current);
    setPerfectSaladBowlFloat({ key: Date.now(), bonus });
    perfectSaladTimerRef.current = setTimeout(() => {
      setPerfectSaladBowlFloat(null);
      perfectSaladTimerRef.current = null;
    }, PERFECT_SALAD_BOWL_FLOAT_MS);
    return bonus;
  };

  const runInterRoundAdvance = (completedRounds, lastRoundIndex) => {
    setInterRoundOverlay(true);
    window.setTimeout(() => {
      persistWelcomeStageProgress(completedRounds);
      setActiveRoundIndex((prev) => Math.min(prev + 1, lastRoundIndex));
      setInterRoundOverlay(false);
      setStageTransitioning(false);
    }, 2400);
  };

  /** After a mid-game round: run perfect bonus (bowl float), then wait for float to finish before “next salad” overlay. */
  const scheduleInterRoundAfterPerfectFloat = (completedRounds, lastRoundIndex) => {
    const bonus = maybeAwardPerfectRoundBonus();
    const waitMs = bonus > 0 ? PERFECT_SALAD_BOWL_FLOAT_MS : 0;
    window.setTimeout(() => {
      runInterRoundAdvance(completedRounds, lastRoundIndex);
    }, waitMs);
  };

  const performBowlDrop = (dishId) => {
    if (gameComplete || isStageTransitioningRef.current) return;
    setBowlDragOver(false);
    setDraggedDishId(null);
    if (!dishId) return;

    const droppedDish = visibleDishCards.find((dish) => dish.__dishId === dishId);
    const sosLayersById = {
      "sos-1": sos1LayeredImage,
      "sos-2": sos2LayeredImage,
      "sos-3": sos3LayeredImage,
    };
    if (!droppedDish && !sosLayersById[dishId]) return;

    if (sosLayersById[dishId]) {
      if (bowlLayeredItemsRef.current.some((item) => item.id === dishId)) return;
      setAnimatingSosId(dishId);
      setDraggedDishId(dishId);
      setTimeout(() => {
        setAnimatingSosId("");
        setDraggedDishId("");
        if (bowlLayeredItemsRef.current.some((item) => item.id === dishId)) return;
        const nextBowlLayeredItems = [
          ...bowlLayeredItemsRef.current,
          { id: dishId, src: sosLayersById[dishId] },
        ];
        bowlLayeredItemsRef.current = nextBowlLayeredItems;
        setBowlLayeredItems(nextBowlLayeredItems);
        const nextSosDroppedCount = stageRequiredSosIds.filter((id) =>
          nextBowlLayeredItems.some((item) => item.id === id)
        ).length;
        const nextStageProgress = droppedPositiveDishIdsRef.current.length + nextSosDroppedCount;
        if (isDemoBypass && nextStageProgress === 1) {
          redirectDemoUserToPlans();
          return;
        }
        if (stageCompletionTarget > 0 && nextStageProgress >= stageCompletionTarget) {
          setStageTransitioning(true);
          if (activeRoundIndex >= roundList.length - 1) {
            setTimeout(async () => {
              setStageTransitioning(false);
              maybeAwardPerfectRoundBonus();
              await completeGame();
            }, 420);
          } else {
            const completedRounds = activeRoundIndex + 1;
            setTimeout(() => {
              scheduleInterRoundAfterPerfectFloat(completedRounds, roundList.length - 1);
            }, 520);
          }
        }
      }, 700);
      return;
    }

    const alreadyDropped = droppedPositiveDishIdsRef.current.includes(dishId);
    const isPositive = String(droppedDish.type || "").toLowerCase() === "positive";
    const popupCandidate = String(droppedDish.popup_text || "").trim();

    if (isPositive) {
      if (alreadyDropped) return;
      const nextDroppedPositiveDishIds = [...droppedPositiveDishIdsRef.current, dishId];
      droppedPositiveDishIdsRef.current = nextDroppedPositiveDishIds;
      setDroppedPositiveDishIds(nextDroppedPositiveDishIds);
      if (droppedDish.dish_layered_img) {
        setBowlLayeredItems((prev) => [
          ...prev,
          {
            id: dishId,
            src: getDvSourceImageUrl(droppedDish.dish_layered_img),
          },
        ]);
      }
      setPoints((p) => {
        const next = p + adminRightPoints;
        pointsRef.current = next;
        return next;
      });
      if (bowlFloatTimerRef.current) clearTimeout(bowlFloatTimerRef.current);
      setBowlPointsFloat({ key: Date.now(), amount: adminRightPoints });
      bowlFloatTimerRef.current = setTimeout(() => {
        setBowlPointsFloat(null);
        bowlFloatTimerRef.current = null;
      }, 1200);
      const sosDroppedCount = stageRequiredSosIds.filter((id) =>
        bowlLayeredItemsRef.current.some((item) => item.id === id)
      ).length;
      const nextStageProgress = nextDroppedPositiveDishIds.length + sosDroppedCount;
      if (isDemoBypass && nextStageProgress === 1) {
        redirectDemoUserToPlans();
        return;
      }
      if (stageCompletionTarget > 0 && nextStageProgress >= stageCompletionTarget) {
        setStageTransitioning(true);
        if (activeRoundIndex >= roundList.length - 1) {
          setTimeout(async () => {
            setStageTransitioning(false);
            maybeAwardPerfectRoundBonus();
            await completeGame();
          }, 420);
        } else {
          const completedRounds = activeRoundIndex + 1;
          setTimeout(() => {
            scheduleInterRoundAfterPerfectFloat(completedRounds, roundList.length - 1);
          }, 520);
        }
      }
      return;
    }

    setWrongCount((w) => w + 1);
    wrongDropsThisRoundRef.current += 1;
    setWrongDropDishId(dishId);
    setTimeout(() => setWrongDropDishId(null), 500);
    setActiveDishPopupImage("");
    setActiveDishPopupText(popupCandidate || "");
    setPopupBubbleKey((k) => k + 1);
    if (dishPopupTimerRef.current) clearTimeout(dishPopupTimerRef.current);
    dishPopupTimerRef.current = setTimeout(() => {
      setActiveDishPopupText("");
      dishPopupTimerRef.current = null;
    }, 2200);
  };

  const finishPointerDragSession = (clientX, clientY) => {
    const session = pointerSessionRef.current;
    pointerSessionRef.current = null;
    setBowlDragOver(false);

    if (session?.ghostEl?.parentNode) {
      session.ghostEl.parentNode.removeChild(session.ghostEl);
    }

    const bowl = bowlDropZoneRef.current;
    let over = false;
    if (bowl && session?.payloadId) {
      const r = bowl.getBoundingClientRect();
      over =
        clientX >= r.left &&
        clientX <= r.right &&
        clientY >= r.top &&
        clientY <= r.bottom;
    }

    if (over && session?.payloadId) {
      performBowlDrop(session.payloadId);
    } else {
      setDraggedDishId(null);
    }
  };

  const attachWelcomePointerDrag = (e, payloadId, ghostSetup) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    if (gameComplete || isStageTransitioningRef.current) return;
    if (pointerSessionRef.current) return;
    e.preventDefault();
    e.stopPropagation();

    const { ghostEl, offsetX, offsetY } = ghostSetup;
    ghostEl.style.position = "fixed";
    ghostEl.style.left = `${e.clientX - offsetX}px`;
    ghostEl.style.top = `${e.clientY - offsetY}px`;
    ghostEl.style.zIndex = "10000";
    ghostEl.style.pointerEvents = "none";
    document.body.appendChild(ghostEl);

    setDraggedDishId(payloadId);
    const pointerId = e.pointerId;
    const captureTarget = e.currentTarget;
    pointerSessionRef.current = { pointerId, ghostEl, offsetX, offsetY, payloadId };

    try {
      captureTarget.setPointerCapture(pointerId);
    } catch {
      /* ignore */
    }

    const onMove = (ev) => {
      if (ev.pointerId !== pointerId) return;
      ev.preventDefault();
      const sess = pointerSessionRef.current;
      if (!sess?.ghostEl) return;
      sess.ghostEl.style.left = `${ev.clientX - sess.offsetX}px`;
      sess.ghostEl.style.top = `${ev.clientY - sess.offsetY}px`;
      const bowl = bowlDropZoneRef.current;
      if (bowl) {
        const r = bowl.getBoundingClientRect();
        const hover =
          ev.clientX >= r.left &&
          ev.clientX <= r.right &&
          ev.clientY >= r.top &&
          ev.clientY <= r.bottom;
        setBowlDragOver(hover);
      }
    };

    const onUp = (ev) => {
      if (ev.pointerId !== pointerId) return;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      try {
        captureTarget.releasePointerCapture(pointerId);
      } catch {
        /* ignore */
      }
      finishPointerDragSession(ev.clientX, ev.clientY);
    };

    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const onWelcomeDishPointerDown = (e, dish) => {
    if (!dish?.__dishId || droppedPositiveDishIds.includes(dish.__dishId)) return;
    if (!dish?.dish_img) return;
    const ghost = document.createElement("img");
    ghost.src = getDvSourceImageUrl(dish.dish_img);
    ghost.style.width = "88px";
    ghost.style.height = "88px";
    ghost.style.objectFit = "contain";
    attachWelcomePointerDrag(e, dish.__dishId, { ghostEl: ghost, offsetX: 44, offsetY: 44 });
  };

  const onWelcomeSosPointerDown = (e, sosId, src) => {
    if (!sosId || !src) return;
    const ghost = document.createElement("img");
    ghost.src = src;
    ghost.style.width = "42px";
    ghost.style.height = "90px";
    ghost.style.objectFit = "contain";
    attachWelcomePointerDrag(e, sosId, { ghostEl: ghost, offsetX: 21, offsetY: 45 });
  };

  return (
    <div
      className={`game-container bin-toss-game-touch${draggedDishId ? " dragging-active" : ""}${
        isMobilePortrait ? " bin-toss--mobile-portrait" : ""
      }${isMobileLandscape ? " bin-toss--mobile-landscape" : ""}`}
    >
      {viewBg ? (
        <div style={{ position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none" }} className="view-bg">
          <div className="target-zone" style={{ position: "absolute", left: "8%", top: "23%", width: "24%" }}>
            <img src={cat1Img} alt="" style={{ width: "100%", height: "auto", display: "block" }} />
            <div className="dropped-items dropped-items-paper" aria-hidden="true">
              {(droppedByCategory?.[1] || []).map((it, i) => (
                <div key={it.id} className="paper-drop" style={{ zIndex: 1 + i }}>
                  <img src={it.url} alt="" draggable={false} />
                </div>
              ))}
            </div>
          </div>
          <div className="target-zone" style={{ position: "absolute", left: "38%", top: "23%", width: "24%" }}>
            <img src={cat2Img} alt="" style={{ width: "100%", height: "auto", display: "block" }} />
            <div className="dropped-items dropped-items-bin" aria-hidden="true">
              {(droppedByCategory?.[2] || []).map((it, i) => (
                <img key={it.id} className="bin-drop-item" src={it.url} alt="" draggable={false} style={{ zIndex: 5 + i }} />
              ))}
            </div>
          </div>
          <div className="target-zone" style={{ position: "absolute", left: "68%", top: "23%", width: "24%" }}>
            <img src={cat3Img} alt="" style={{ width: "100%", height: "auto", display: "block" }} />
            <div className="dropped-items dropped-items-bin" aria-hidden="true">
              {(droppedByCategory?.[3] || []).map((it, i) => (
                <img key={it.id} className="bin-drop-item" src={it.url} alt="" draggable={false} style={{ zIndex: 5 + i }} />
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <div className="welcome-round-overlay" aria-hidden="true">
        <div className="welcome-round-recipe-bubble">
          {Boolean(openedRecipeHintsByRound[activeRoundKey]) ? (
            <div
              key={`recipe-open-${activeRoundKey}`}
              className="welcome-round-recipe-image-wrap welcome-round-recipe-image-wrap--enter"
            >
              <img
                src={getDvSourceImageUrl(activeRound.required_recipe_image)}
                alt="Required recipe"
                className="welcome-round-recipe-image"
              />
            </div>
          ) : null}
        </div>

        <div className="welcome-round-card">
          <div
            className="welcome-round-badge"
            style={{
              ...(themeData?.button_color ? { background: themeData.button_color } : null),
              ...(themeData?.button_Textcolor
                ? { color: themeData.button_Textcolor, borderColor: themeData.button_Textcolor }
                : null),
            }}
          >
            ROUND {Number(activeRound?.round_id) || 1}
          </div>
          <div
            className="welcome-round-title"
            style={themeData?.button_Textcolor ? { color: themeData.button_color } : undefined}
          >
            {activeRound?.title || ""}
          </div>
          <div
            className="welcome-round-question"
            style={landingTitleColorCss ? { color: landingTitleColorCss } : undefined}
          >
            {activeRound?.question || ""}
          </div>
        </div>

        {activeDishPopupText ? (
          <div
            key={`wrong-popup-${popupBubbleKey}`}
            className="welcome-round-popup-bubble welcome-round-popup-bubble--text welcome-round-popup-bubble--enter"
            style={
              popupBgImage
                ? { backgroundImage: `url(${popupBgImage})` }
                : undefined
            }
          >
            <div
              className="welcome-round-popup-text"
              style={landingTitleColorCss ? { color: landingTitleColorCss } : undefined}
            >
              {activeDishPopupText}
            </div>
          </div>
        ) : null}
      </div>

      {tableImage ? (
        <div className="welcome-table-wrap" aria-hidden="true">
          <img src={tableImage} alt="" className="welcome-table-image" />
          {bowlImage ? (
            <div
              ref={bowlDropZoneRef}
              className={`welcome-table-bowl-dropzone ${bowlDragOver ? "is-over" : ""}`}
            >
              <img src={bowlImage} alt="" className="welcome-table-bowl-image" />
              {animatingSosId ? (
                <img
                  src={
                    animatingSosId === "sos-1"
                      ? sos1Image
                      : animatingSosId === "sos-2"
                      ? sos2Image
                      : sos3Image
                  }
                  alt=""
                  className={`welcome-bowl-pour-bottle ${
                    animatingSosId === "sos-1"
                      ? "pour-bottle-sos-1"
                      : animatingSosId === "sos-2"
                      ? "pour-bottle-sos-2"
                      : "pour-bottle-sos-3"
                  }`}
                  aria-hidden="true"
                />
              ) : null}
              {animatingSosId ? (
                <div
                  className={`welcome-bowl-sos-pour ${
                    animatingSosId === "sos-1"
                      ? "pour-sos-1"
                      : animatingSosId === "sos-2"
                      ? "pour-sos-2"
                      : "pour-sos-3"
                  }`}
                  aria-hidden="true"
                />
              ) : null}
              {bowlPointsFloat ? (
                <span
                  key={bowlPointsFloat.key}
                  className="welcome-bowl-points-float"
                  aria-hidden="true"
                >
                  +{bowlPointsFloat.amount}
                </span>
              ) : null}
              {perfectSaladBowlFloat ? (
                <span
                  key={perfectSaladBowlFloat.key}
                  className="welcome-bowl-perfect-float"
                  aria-live="polite"
                >
                  Perfect salad +{perfectSaladBowlFloat.bonus}
                </span>
              ) : null}
              <div className="welcome-bowl-layered-items" aria-hidden="true">
                {bowlLayeredItems.map((item, idx) => (
                  <img
                    key={item.id}
                    src={item.src}
                    alt=""
                    className="welcome-bowl-layered-image"
                    style={{
                      left: "50%",
                      top: "50%",
                      transform: `translate(-50%, -50%) translate(${(idx % 3) * 2 - 2}px, ${Math.floor(idx / 3)}px)`,
                      zIndex: idx + 1,
                    }}
                  />
                ))}
              </div>
            </div>
          ) : null}
          <div className="welcome-table-sos-row">
            {sos1Image ? (
              <img
                src={sos1Image}
                alt=""
                draggable={false}
                onPointerDown={(e) => onWelcomeSosPointerDown(e, "sos-1", sos1Image)}
                className={`welcome-table-sos-item welcome-table-sos-item-1${
                  draggedDishId === "sos-1" || animatingSosId === "sos-1" ? " is-hidden-during-drag" : ""
                }`}
              />
            ) : null}
            {sos2Image ? (
              <img
                src={sos2Image}
                alt=""
                draggable={false}
                onPointerDown={(e) => onWelcomeSosPointerDown(e, "sos-2", sos2Image)}
                className={`welcome-table-sos-item welcome-table-sos-item-2${
                  draggedDishId === "sos-2" || animatingSosId === "sos-2" ? " is-hidden-during-drag" : ""
                }`}
              />
            ) : null}
            {sos3Image ? (
              <img
                src={sos3Image}
                alt=""
                draggable={false}
                onPointerDown={(e) => onWelcomeSosPointerDown(e, "sos-3", sos3Image)}
                className={`welcome-table-sos-item welcome-table-sos-item-3${
                  draggedDishId === "sos-3" || animatingSosId === "sos-3" ? " is-hidden-during-drag" : ""
                }`}
              />
            ) : null}
          </div>
          <div className="welcome-table-dishes-arc">
            {visibleDishCards.map((dish, idx) => (
              <div
                key={`${dish?.dish_name || "dish"}-${idx}`}
                className="welcome-table-dish-item"
                style={dishArcPositions[idx] || dishArcPositions[dishArcPositions.length - 1]}
              >
                <div
                  className="welcome-table-dish-name"
                  style={gameTextColorCss ? { color: gameTextColorCss } : undefined}
                >
                  {dish?.dish_name || ""}
                </div>
                <div
                  className={`welcome-table-dish-plate-wrap ${
                    wrongDropDishId === dish.__dishId ? "revert-wrong" : ""
                  }`}
                >
                  {plateImage ? (
                    <img
                      src={plateImage}
                      alt=""
                      className="welcome-table-plate-image"
                    />
                  ) : null}
                  {dish?.dish_img ? (
                    <img
                      src={getDvSourceImageUrl(dish.dish_img)}
                      alt={dish?.dish_name || `Dish ${idx + 1}`}
                      className={`welcome-table-dish-image ${
                        draggedDishId === dish.__dishId ? "is-dragging is-hidden-during-drag" : ""
                      }`}
                      draggable={false}
                      onPointerDown={(e) => onWelcomeDishPointerDown(e, dish)}
                      style={{ visibility: droppedPositiveDishIds.includes(dish.__dishId) ? "hidden" : "visible" }}
                    />
                  ) : null}
                </div>
                <div
                  className="welcome-table-dish-category"
                  style={gameTextColorCss ? { color: gameTextColorCss } : undefined}
                >
                  {dish?.dish_category_name || ""}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

   
      <div
        style={{
          position: "absolute",
          left: "6%",
          right: "6%",
          top: "12%",
          zIndex: 7,
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: "10px",
          pointerEvents: "none",
        }}
      >
        {[1, 2, 3].map((n) => (
          <div
            key={`cat-title-${n}`}
            className="feedback-layout-cat-title"
            style={{
              textAlign: "center",
              fontSize: isMobilePortrait ? "14px" : "18px",
              lineHeight: 1.1,
              ...(gameTextColorCss ? { color: gameTextColorCss } : null),
            }}
          >
            {String(themeData?.[`category${n}_title`] ?? "").trim()}
          </div>
        ))}
      </div>

      <div style={{ position: "relative", zIndex: 15 }}>
        <header
          className="game-hud-bar game-hud-bar--welcome"
          aria-label="Game status"
          style={{
            ...(themeData?.button_color ? { "--welcome-hud-bg": themeData.button_color } : null),
            ...(themeData?.button_Textcolor
              ? { "--welcome-hud-text": themeData.button_Textcolor }
              : null),
          }}
        >
          <div className="game-hud-time game-hud-time--welcome">
            <span id="timer" className="game-hud-pocket game-hud-pocket--timer">
              {formatTime()}
            </span>
            <div className="game-hud-actions-stack">
              <button
                type="button"
                className="game-hud-action-btn game-hud-action-btn--question"
                onClick={() => setShowRulesPopup(true)}
                aria-label="How to play"
              >
                ?
              </button>
              <button
                type="button"
                className="game-hud-action-btn game-hud-action-btn--hint"
                onClick={handleHintClick}
                aria-label="Show recipe hint"
              >
                <img src={hintActionIcon} alt="" />
              </button>
            </div>
          </div>
          <div className="game-hud-title-box" style={{ display: "none" }}>
            <h3
              className="game-hud-title-text log-title bin-toss-instruction"
              style={{
                backgroundColor: hudTitleBoxBg,
                "--game-hud-title-box-bg": hudTitleBoxBg,
                ...(gameTextColorCss ? { color: gameTextColorCss } : null),
                ...(!isMobilePortrait && titleDesktopFontSizePx != null
                  ? { fontSize: `${titleDesktopFontSizePx}px` }
                  : null),
              }}
            >
              {themeData?.landing_page_title}
            </h3>
          </div>

          <div className="game-hud-points game-hud-points--welcome">
            <div className="game-hud-pocket game-hud-pocket--points">
              <img src={pointIcon} alt="" className="game-hud-point-icon" width={28} height={28} />
              <div className="game-hud-points-inner">
                <span id="score" className="game-hud-points-value game-hud-points-value--in-pocket">
                  {points}
                </span>
              </div>
            </div>
          </div>
        </header>
      </div>

      {interRoundOverlay ? (
        <div
          className="welcome-inter-round-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label="Round complete"
        >
          <div className="welcome-inter-round-card">
            <span className="welcome-inter-round-emoji" aria-hidden="true">
              🥗
            </span>
            <span className="welcome-inter-round-title">Delicious!</span>
            <span className="welcome-inter-round-sub">Moving on to the next recipe </span>
          </div>
        </div>
      ) : null}

      {showRulesPopup ? (
        <div className="welcome-overlay-backdrop" role="presentation">
          <div className="welcome-rules-card" role="dialog" aria-modal="true" aria-label="How to play">
            <h3 style={landingTitleColorCss ? { color: landingTitleColorCss } : undefined}>HOW TO PLAY</h3>
            <div className="welcome-rules-list">
              {(rulesList.length ? rulesList : ["Follow the on-screen instructions and sort correctly."]).map(
                (rule, idx) => (
                  <div key={`rule-${idx}`} className="welcome-rule-item">
                    <span className="welcome-rule-box">☐</span>
                    <span style={landingTitleColorCss ? { color: landingTitleColorCss } : undefined}>{rule}</span>
                  </div>
                )
              )}
            </div>
            <button
              type="button"
              className="welcome-rules-close-btn"
              onClick={() => setShowRulesPopup(false)}
              style={{
                ...(themeData?.button_color ? { background: themeData.button_color } : null),
                ...(themeData?.button_Textcolor ? { color: themeData.button_Textcolor } : null),
              }}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

    </div>
  );
}

export default Welcome;
